import {
  CHART_REGISTRY,
  COMPANY_METRICS_TABS,
  getAllCharts,
  getChart,
} from "@/lib/companyMetrics/chartRegistry";
import { METRIC_REGISTRY, getMetric } from "@/lib/companyMetrics/metricRegistry";
import { getMetricSubtitle, getMetricWisdom } from "@/lib/companyMetrics/metricWisdomRegistry";
import { buildMetricViewUrl } from "./researchSearchUrl";

const TAB_LABELS = Object.fromEntries(
  COMPANY_METRICS_TABS.map((tab) => [tab.id, tab.label]),
);

const TAB_ALIASES = {
  financials: ["financials", "financial", "income statement", "income"],
  cashflow: ["cash flow", "cashflow", "cash-flow"],
  margins: ["margins", "margin"],
  quality: ["quality", "moat", "returns"],
  balance: ["balance sheet", "balance", "leverage"],
  growth: ["growth"],
  valuation: ["valuation", "valuations", "multiples", "value"],
  capital: ["capital allocation", "capital", "buybacks", "buyback", "dividends", "sbc", "dilution"],
  comparisons: ["comparisons", "raw comparisons", "comparison", "comparison charts", "a6"],
};

const SEARCH_ALIASES = {
  revenue: ["revenue"],
  ttm: ["revenue"],
  "trailing twelve months": ["revenue"],
  fcf: ["free_cash_flow"],
  "free cash flow": ["free_cash_flow"],
  "fcf margin": ["fcf_margin"],
  "fcf yield": ["fcf_yield"],
  roic: ["roic"],
  "return on invested capital": ["roic"],
  roe: ["roe"],
  roa: ["roa"],
  roce: ["roce"],
  sbc: ["stock_based_compensation"],
  "stock based compensation": ["stock_based_compensation"],
  "stock-based compensation": ["stock_based_compensation"],
  pe: ["trailing_pe"],
  "p/e": ["trailing_pe"],
  "trailing pe": ["trailing_pe"],
  "trailing p/e": ["trailing_pe"],
  "forward pe": ["forward_pe"],
  "forward p/e": ["forward_pe"],
  ps: ["ev_to_sales"],
  "p/s": ["ev_to_sales"],
  "price to sales": ["ev_to_sales"],
  "price-to-sales": ["ev_to_sales"],
  "ev/sales": ["ev_to_sales"],
  "ev/s": ["ev_to_sales"],
  "ev/ebitda": ["ev_to_ebitda"],
  "debt/ebitda": ["net_debt_to_ebitda"],
  "net debt/ebitda": ["net_debt_to_ebitda"],
  peg: ["peg"],
  "eps revision": ["eps_revision_trend"],
  drawdown: ["drawdown_pct"],
  "200dma": ["price_vs_200dma"],
  "200 dma": ["price_vs_200dma"],
  "200-day moving average": ["price_vs_200dma"],
  "price vs 200dma": ["price_vs_200dma"],
  rsi: ["rsi_14"],
  ebitda: ["ebitda"],
  ocf: ["operating_cash_flow"],
  capex: ["capital_expenditure"],
  "operating cash flow": ["operating_cash_flow"],
  "net debt": ["net_debt"],
  "fcf conversion": ["fcf_conversion"],
  buybacks: ["share_buybacks"],
  buyback: ["share_buybacks"],
  "share buybacks": ["share_buybacks"],
  dividends: ["dividends_paid"],
  "dividends paid": ["dividends_paid"],
  "shares outstanding": ["shares_outstanding"],
  dilution: ["shares_outstanding"],
  acquisitions: ["acquisitions_net"],
  "growth vs pe": ["growth_vs_pe"],
  "growth vs p/e": ["growth_vs_pe"],
  "price vs fcf": ["price_vs_fcf_growth"],
  "price vs fcf growth": ["price_vs_fcf_growth"],
};

const KNOWN_METRIC_TOKENS = new Set([
  "ROIC", "ROE", "ROA", "ROCE", "FCF", "PE", "PS", "PEG", "RSI", "OCF", "SBC",
  "TTM", "NTM", "YOY", "QOQ", "EBITDA", "EPS", "EV", "DMA", "ATH", "CAGR",
]);

const RANGE_TOKENS = {
  "1Y": "1Y",
  "3Y": "3Y",
  "5Y": "5Y",
  "10Y": "10Y",
  MAX: "Max",
};

function toSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-")
    .replace(/[^\w\s/-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/\/+/g, "-");
}

function tokenizeQuery(query = "") {
  return String(query)
    .toLowerCase()
    .replace(/[^\w\s/.-]/g, " ")
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function looksLikeTicker(token) {
  if (KNOWN_METRIC_TOKENS.has(token)) return false;
  return /^[A-Z]{1,6}$/.test(token) || /^[A-Z]{1,6}\.[A-Z]{1,4}$/.test(token);
}

export function chartForMetric(metricId) {
  const charts = getAllCharts().filter((chart) => chart.metricIds?.includes(metricId));
  const single = charts.find((chart) => chart.metricIds.length === 1);
  return single || charts[0] || null;
}

function buildMetricIndexEntry(metricId) {
  const metric = getMetric(metricId);
  const wisdom = getMetricWisdom(metricId);
  const chart = chartForMetric(metricId);
  if (!metric && !wisdom) return null;

  return {
    metricId,
    chartId: chart?.id || null,
    chartTitle: chart?.title || metric?.label || wisdom?.label || metricId,
    tabId: chart?.tab || wisdom?.group || null,
    tabLabel: chart?.tab ? TAB_LABELS[chart.tab] || chart.tab : null,
    subtitle: wisdom?.subtitle || getMetricSubtitle(metricId),
    label: metric?.label || wisdom?.label || metricId,
    shortLabel: wisdom?.shortLabel,
    available: Boolean(chart),
    what: wisdom?.what || "",
    why: wisdom?.why || "",
    watch: wisdom?.watch || "",
  };
}

function buildChartIndexEntry(chart) {
  const primaryMetricId = chart.metricIds?.[0];
  const wisdom = primaryMetricId ? getMetricWisdom(primaryMetricId) : null;

  return {
    chartId: chart.id,
    chartSlug: getChartSlug(chart.id),
    metricId: primaryMetricId,
    metricIds: chart.metricIds || [],
    chartTitle: chart.title,
    tabId: chart.tab,
    tabLabel: TAB_LABELS[chart.tab] || chart.tab,
    subtitle: wisdom?.subtitle || "",
    available: true,
  };
}

let cachedIndex = null;

export function buildResearchSearchIndex() {
  if (cachedIndex) return cachedIndex;

  const metrics = Object.keys(METRIC_REGISTRY)
    .map(buildMetricIndexEntry)
    .filter(Boolean);
  const charts = getAllCharts().map(buildChartIndexEntry);

  cachedIndex = { metrics, charts, tabLabels: TAB_LABELS };
  return cachedIndex;
}

export function getChartSlug(chartId) {
  if (!chartId) return "";
  const parts = String(chartId).split(".");
  return toSlug(parts[parts.length - 1]);
}

function resolveTabFromTokens(tokens) {
  const joined = tokens.join(" ");
  for (const [tabId, aliases] of Object.entries(TAB_ALIASES)) {
    if (aliases.some((alias) => joined.includes(alias))) return tabId;
    if (tokens.includes(tabId)) return tabId;
  }
  return null;
}

function resolveMetricIdsFromQuery(raw, tokens) {
  const joined = tokens.join(" ");
  const metricIds = new Set();

  Object.entries(SEARCH_ALIASES).forEach(([alias, ids]) => {
    if (joined.includes(alias)) ids.forEach((id) => metricIds.add(id));
  });

  const index = buildResearchSearchIndex();
  tokens.forEach((token) => {
    const metricEntry = index.metrics.find((m) =>
      m.metricId === token.replace(/-/g, "_")
      || m.label?.toLowerCase() === token
      || m.metricId.replace(/_/g, "") === token.replace(/-/g, ""),
    );
    if (metricEntry) metricIds.add(metricEntry.metricId);
  });

  return [...metricIds];
}

function parseRangeFromTokens(upperTokens) {
  for (const token of upperTokens) {
    const normalized = token.toUpperCase();
    if (RANGE_TOKENS[normalized]) return RANGE_TOKENS[normalized];
    if (/^\d+Y$/.test(normalized) && RANGE_TOKENS[normalized]) return RANGE_TOKENS[normalized];
  }
  if (upperTokens.some((t) => t.toUpperCase() === "10Y")) return "10Y";
  return null;
}

function pickPrimaryMetric(metricIds, tokens = []) {
  if (!metricIds.length) return null;
  if (metricIds.length === 1) return metricIds[0];
  const joined = tokens.join(" ");
  if ((joined.includes("growth vs") || joined.includes("growth-vs")) && metricIds.includes("growth_vs_pe")) {
    return "growth_vs_pe";
  }
  if ((joined.includes("price vs fcf") || joined.includes("price-vs-fcf")) && metricIds.includes("price_vs_fcf_growth")) {
    return "price_vs_fcf_growth";
  }
  if (joined.includes("fcf margin") && metricIds.includes("fcf_margin")) return "fcf_margin";
  if (joined.includes("forward") && metricIds.includes("forward_pe")) return "forward_pe";
  if (joined.includes("trailing") && metricIds.includes("trailing_pe")) return "trailing_pe";
  const priority = ["growth_vs_pe", "price_vs_fcf_growth", "forward_pe", "trailing_pe", "fcf_margin", "revenue", "free_cash_flow", "roic"];
  return priority.find((id) => metricIds.includes(id)) || metricIds[0];
}

/**
 * Resolve a research search query into a navigation destination.
 * Returns { destination, path, ticker, metricId, tabId, controls, metric }
 */
export function resolveResearchQuery(query = "", options = {}) {
  const raw = String(query || "").trim();
  if (!raw) {
    return { destination: "home", query: raw };
  }

  const upperTokens = raw.split(/\s+/).filter(Boolean);
  const tokens = tokenizeQuery(raw);
  const tickers = upperTokens.map((t) => t.toUpperCase()).filter(looksLikeTicker);
  const metricIds = resolveMetricIdsFromQuery(raw, tokens);
  const tabId = resolveTabFromTokens(tokens);
  const range = parseRangeFromTokens(upperTokens);
  const controls = range ? { range } : {};

  // Metric-only → Metric Guide
  if (!tickers.length && metricIds.length && !tabId) {
    const metricId = pickPrimaryMetric(metricIds, tokens);
    const metric = buildMetricIndexEntry(metricId);
    return {
      destination: "metric-guide",
      query: raw,
      metricId,
      metric,
      path: `/research/metrics/${metricId}`,
      controls,
    };
  }

  const ticker = tickers[0] || options.resolvedTicker || null;

  // Company + tab category → company metrics tab
  if (ticker && tabId && !metricIds.length) {
    return {
      destination: "tab-view",
      query: raw,
      ticker,
      tabId,
      tabLabel: TAB_LABELS[tabId],
      path: `/research/${ticker}?tab=${tabId}`,
      controls,
    };
  }

  // Company + metric → Metric View
  if (ticker && metricIds.length) {
    const metricId = pickPrimaryMetric(metricIds, tokens);
    const metric = buildMetricIndexEntry(metricId);
    return {
      destination: "metric-view",
      query: raw,
      ticker,
      metricId,
      metric,
      chartId: metric?.chartId,
      path: buildMetricViewUrl(ticker, metricId, controls),
      controls,
    };
  }

  // Company-only → company metrics view (Business Cockpit later)
  if (ticker && !metricIds.length && !tabId) {
    return {
      destination: "business-cockpit",
      query: raw,
      ticker,
      path: `/research/${ticker}`,
      controls,
    };
  }

  // Name-only company search fallback — caller resolves ticker via market API
  if (!tickers.length && !metricIds.length && !tabId) {
    return {
      destination: "company-lookup",
      query: raw,
      path: null,
      controls,
    };
  }

  return { destination: "empty", query: raw, controls };
}

export function getMetricById(metricId) {
  return buildMetricIndexEntry(metricId);
}

export function getChartsForTabId(tabId) {
  const tab = COMPANY_METRICS_TABS.find((t) => t.id === tabId);
  if (!tab) return [];
  return tab.chartIds.map((id) => buildChartIndexEntry(getChart(id))).filter(Boolean);
}

/** @deprecated use resolveResearchQuery */
export function searchChartExplorer(query, options) {
  const resolved = resolveResearchQuery(query, options);
  return { mode: resolved.destination, ...resolved };
}

/** Preferred alias for searchChartExplorer */
export const searchResearchDesk = searchChartExplorer;

export function resolveChartSlug(slug) {
  const normalized = toSlug(slug);
  if (CHART_REGISTRY[normalized]) return normalized;
  const index = buildResearchSearchIndex();
  const bySlug = index.charts.find((c) => c.chartSlug === normalized);
  if (bySlug) return bySlug.chartId;
  const metricChart = chartForMetric(normalized.replace(/-/g, "_"));
  return metricChart?.id || null;
}

export { TAB_LABELS, toSlug };
